import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import api from "config/api";
import { format } from "date-fns";
import moment from "moment";
import { NavigateFunction } from "react-router-dom";
import { toast } from "react-toastify";
import { AppDispatch } from "store";
import {
  CompanyData,
  ProductType,
  SettingType,
  SideBarItem,
  TaxesType,
  TimeItem,
} from "utils/types";
import { getUnavailableCartItemsForOrderType } from "utils/orderTypeAvailability";
import { showApiErrorToast } from "utils/apiToast";
import { TIME_ZONES } from "utils/constants";

const sanitizeCartPayload = (value: any): any => {
  const seen = new WeakMap<object, any>();
  const omitKeys = new Set([
    "sourceDealPizza",
    "existingFullNote",
    "existingPizzaSize",
    "existingSizePrice",
    "existingPizzaCrust",
    "existingSpiceLevel",
    "existingSubTotal",
    "legacyQuantity",
    "selected",
  ]);

  const visit = (input: any): any => {
    if (input === null || typeof input !== "object") return input;
    if (seen.has(input)) return seen.get(input);

    if (
      input?.selected === false &&
      (Object.prototype.hasOwnProperty.call(input, "pizzaModifierId") ||
        Object.prototype.hasOwnProperty.call(input, "modifierId"))
    ) {
      return undefined;
    }

    if (Array.isArray(input)) {
      const arr = input.map(visit).filter((item) => item !== undefined);
      seen.set(input, arr);
      return arr;
    }

    const out: any = {};
    seen.set(input, out);

    Object.keys(input).forEach((key) => {
      if (omitKeys.has(key)) return;
      if (key === "pizzaRegion") {
        out.PizzaRegion = visit(input[key]);
        return;
      }
      out[key] = visit(input[key]);
    });

    if (
      (Object.prototype.hasOwnProperty.call(out, "productId") ||
        Object.prototype.hasOwnProperty.call(out, "id")) &&
      !Object.prototype.hasOwnProperty.call(out, "prodcat")
    ) {
      out.prodcat =
        input?.prodcat ??
        input?.prodCat ??
        input?.categoryId ??
        input?.__categoryId ??
        input?.data?.prodcat ??
        input?.data?.prodCat ??
        input?.data?.categoryId ??
        input?.data?.__categoryId ??
        0;
    }

    if (
      Object.prototype.hasOwnProperty.call(out, "modifierId") &&
      Object.prototype.hasOwnProperty.call(out, "quantity")
    ) {
      const multiplier = Math.max(
        1,
        Number(input?.Multiplier || input?.DtltoppMultiplier || input?.legacyQuantity || 1)
      );
      // Cart API model expects an integer. Final order submit keeps the 0.5 side quantity.
      out.quantity = multiplier;
      out.isMultiplier =
        Number(input?.isMultiplier ?? input?.DtltoppIsmultiplier ?? (multiplier > 1 ? 1 : 0)) || 0;
      out.Multiplier = multiplier;
      delete out.DtltoppIsmultiplier;
      delete out.DtltoppMultiplier;
    }

    return out;
  };

  return visit(value);
};

const toNumber = (value: any, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const formatCurrentProjectDateTime = (projectTimeZoneName?: string) => {
  const now = new Date();
  const ianaTimeZone =
    (projectTimeZoneName && TIME_ZONES[projectTimeZoneName]) ||
    TIME_ZONES["Pacific Standard Time"];

  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: ianaTimeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  } as any).formatToParts(now);
  const value = (type: string) => parts.find((part) => part.type === type)?.value || "00";

  return `${value("year")}-${value("month")}-${value("day")}T${value("hour")}:${value(
    "minute"
  )}:${value("second")}`;
};

const isSpecialDiscountAllowed = (settings: any = {}, orderType = "") =>
  (orderType === "Delivery" && !!Number(settings?.AllowDiscountOnDelivery)) ||
  (orderType === "TakeOut" && !!Number(settings?.AllowDiscountOnPickup));

const getSpecialDiscountPercent = (settings: any = {}, orderType = "") => {
  const percent = toNumber(settings?.OnlineSpecialDiscount);
  return percent > 0 && isSpecialDiscountAllowed(settings, orderType) ? percent : 0;
};

const calculateDiscountAmount = (amount: any, discountPercent: number) =>
  +((toNumber(amount) * discountPercent) / 100).toFixed(2);

const getDiscountableItemTotal = (item: any = {}) => toNumber(item?.subTotal);

const roundMoney = (value: any) => +toNumber(value).toFixed(2);

const getTaxAfterDiscount = (taxAmount: any, discountPercent = 0) =>
  +(
    toNumber(taxAmount) *
    (discountPercent > 0 ? (100 - discountPercent) / 100 : 1)
  ).toFixed(5);

const getSubmitItemTotal = (item: any = {}, discountAmount = 0, discountPercent = 0) => {
  const subTotal = toNumber(item?.subTotal);
  if (!subTotal) return toNumber(item?.itemTotal);

  return roundMoney(
    subTotal -
      toNumber(discountAmount) +
      getTaxAfterDiscount(item?.totalTax1, discountPercent) +
      getTaxAfterDiscount(item?.totalTax2, discountPercent) +
      getTaxAfterDiscount(item?.totalTax3, discountPercent)
  );
};

const getDiscountedTaxFields = (item: any = {}, discountPercent = 0) => {
  const tax1 = getTaxAfterDiscount(item?.totalTax1 ?? item?.tax1, discountPercent);
  const tax2 = getTaxAfterDiscount(item?.totalTax2 ?? item?.tax2, discountPercent);
  const tax3 = getTaxAfterDiscount(item?.totalTax3 ?? item?.tax3, discountPercent);

  return { tax1, tax2, tax3 };
};

const getSubmitModifierQuantity = (modifier: any = {}) => {
  return toNumber(modifier?.quantity, 1);
};

const getSubmitModifierMultiplier = (modifier: any = {}) =>
  Math.max(
    1,
    toNumber(
      modifier?.Multiplier ??
        modifier?.DtltoppMultiplier ??
        modifier?.legacyQuantity ??
        (toNumber(modifier?.quantity, 1) > 1 ? modifier?.quantity : 1),
      1
    )
  );

const getSubmitModifierIsMultiplier = (modifier: any = {}) =>
  toNumber(
    modifier?.isMultiplier ??
      modifier?.DtltoppIsmultiplier ??
      (getSubmitModifierMultiplier(modifier) > 1 ? 1 : 0),
    0
  );

const formatModifierNoteAmount = (value: any) => {
  const amount = toNumber(value);
  if (!amount) return "";
  return Number.isInteger(amount) ? String(amount) : String(+amount.toFixed(2));
};

const buildModifierNote = (modifier: any = {}) => {
  const name = String(
    modifier?.description || modifier?.pizzaModifierName || modifier?.modifierName || ""
  ).trim();
  if (!name) return String(modifier?.note || "");

  const region = toNumber(modifier?.PizzaRegion ?? modifier?.pizzaRegion, 1);
  const sidePrefix = region === 2 ? "L-" : region === 3 ? "R-" : "";
  const quantityPrefix =
    getSubmitModifierMultiplier(modifier) > 1
      ? `${getSubmitModifierMultiplier(modifier)}x `
      : "";
  const amount = formatModifierNoteAmount(modifier?.subTotal ?? modifier?.totalValue);

  return `${sidePrefix}${quantityPrefix}${name}${amount ? ` ${amount}` : ""}`;
};

const normalizeModifierPayload = (modifier: any = {}) => ({
  modifierId: toNumber(modifier?.modifierId || modifier?.pizzaModifierId),
  quantity: getSubmitModifierQuantity(modifier),
  isMultiplier: getSubmitModifierIsMultiplier(modifier),
  Multiplier: getSubmitModifierMultiplier(modifier),
  unitPrice: toNumber(modifier?.unitPrice),
  subTotal: toNumber(modifier?.subTotal),
  description: String(
    modifier?.description || modifier?.pizzaModifierName || modifier?.modifierName || ""
  ),
  tax1: toNumber(modifier?.tax1),
  tax2: toNumber(modifier?.tax2),
  tax3: toNumber(modifier?.tax3),
  totalValue: toNumber(
    modifier?.subTotal ??
      modifier?.totalValue ??
      toNumber(modifier?.subTotal) +
        toNumber(modifier?.tax1) +
        toNumber(modifier?.tax2) +
        toNumber(modifier?.tax3)
  ),
  note: String(modifier?.note || buildModifierNote(modifier)),
  PizzaRegion: toNumber(modifier?.PizzaRegion ?? modifier?.pizzaRegion, 1),
  defaultTopping: toNumber(modifier?.defaultTopping),
  PizzaNo: toNumber(
    modifier?.PizzaNo ??
      modifier?.pizzaNo ??
      modifier?.dtltopp_pizzaitem ??
      modifier?.DtltoppPizzaitem ??
      modifier?.pizzaItemNumber ??
      modifier?.pizzaIndex,
    0,
  ),
});

const normalizeSelectedItemLevelPayload = (modifier: any = {}) => ({
  level: toNumber(
    modifier?.level ??
      modifier?.DtltoppRegionid ??
      modifier?.regionId ??
      modifier?.levelId ??
      modifier?.isSubLevel
  ),
  price: toNumber(
    modifier?.price ?? modifier?.DtltoppPrice ?? modifier?.unitPrice ?? modifier?.subTotal
  ),
  quantity: toNumber(modifier?.quantity ?? modifier?.DtltoppQty, 1),
  id: toNumber(modifier?.id ?? modifier?.DtltoppToppingid ?? modifier?.modifierId),
  name: String(
    modifier?.name ?? modifier?.DtltoppName ?? modifier?.description ?? modifier?.modifierName ?? ""
  ),
  isMultiplier: toNumber(modifier?.isMultiplier ?? modifier?.DtltoppIsmultiplier, 0),
  Multiplier: Math.max(1, toNumber(modifier?.Multiplier ?? modifier?.DtltoppMultiplier, 1)),
  PizzaRegion: toNumber(modifier?.PizzaRegion ?? modifier?.pizzaRegion, 0),
});

const normalizePizzaCrustPayload = (pizzaCrust: any = null) =>
  pizzaCrust
    ? {
        crustId: toNumber(
          pizzaCrust?.crustId ??
            pizzaCrust?.crustID ??
            pizzaCrust?.CrustId ??
            pizzaCrust?.modifierId ??
            pizzaCrust?.id,
        ),
        crustName: String(
          pizzaCrust?.crustName || pizzaCrust?.modifierName || pizzaCrust?.name || "",
        ),
        crustPrice: toNumber(
          pizzaCrust?.crustPrice ?? pizzaCrust?.modifierCost ?? pizzaCrust?.price,
        ),
      }
    : null;

const normalizePizzaSizePayload = (pizzaSize: any = null) =>
  pizzaSize
    ? {
        pizzasizeId: toNumber(pizzaSize?.pizzasizeId || pizzaSize?.sizeId),
        size: String(pizzaSize?.size || pizzaSize?.sizeName || ""),
      }
    : null;

const normalizeSpiceLevelPayload = (spiceLevel: any = null) =>
  spiceLevel
    ? {
        spiceId: toNumber(spiceLevel?.spiceId || spiceLevel?.modifierId, 1),
        spicename: String(spiceLevel?.spicename || spiceLevel?.modifierName || ""),
      }
    : null;

const getPizzaPreferencePayloadFallback = (description: any = "") => {
  const raw = String(description || "")
    .replace(/\\r\\n/g, "\n")
    .replace(/\\n/g, "\n")
    .replace(/\r\n/g, "\n");
  const bracketValues = Array.from(raw.matchAll(/\[([^\]]+)\]/g))
    .map((match) => String(match[1] || "").trim())
    .filter(Boolean);
  const crustMatch = raw.match(/crust\s*:\s*([^\n\r\]*]+)/i);
  const sizeMatch = raw.match(/(?:^|\n)\s*\*+\s*size\s*:\s*([^\n\r]+)/i);
  const spiceMatch = raw.match(/(?:^|\n)\s*spice\s*:\s*([^\n\r]+)/i);
  const size = sizeMatch?.[1]?.trim() || bracketValues[0] || "";
  const spice =
    spiceMatch?.[1]?.trim() ||
    (bracketValues.length > 1 ? bracketValues[bracketValues.length - 1] : "");
  const crust = crustMatch?.[1]?.trim() || "";

  return {
    pizzaCrust: crust
      ? {
          crustId: 0,
          crustName: crust,
          crustPrice: 0,
        }
      : null,
    pizzaSize: size
      ? {
          pizzasizeId: 0,
          size,
        }
      : null,
    spiceLevel: spice
      ? {
          spiceId: 1,
          spicename: spice,
        }
      : null,
  };
};

const getSubmitDescription = (...values: any[]) => {
  const source = values
    .map((value) => String(value ?? "").trim())
    .find(Boolean);

  if (!source) return "";

  return (
    source
      .split(/\r?\n/)
      .map((line) => line.trim())
      .find(Boolean) || ""
  );
};

const appendPreferenceLinesToNote = (note: any, _pizzaSize: any = null, _spiceLevel: any = null) => {
  const baseNote = String(note || "").trim();
  const baseLines = baseNote
    ? baseNote
        .split("\n")
        .map((line) => line.trim())
        .filter(Boolean)
    : [];
  const filteredBaseLines = baseLines.filter(
    (line) =>
      !/^size\s*:/i.test(line) &&
      !/^spice\s*:/i.test(line) &&
      !/^toppings\s*:/i.test(line)
  );

  return filteredBaseLines.filter(Boolean).join("\n");
};

const normalizeDealPizzaPayload = (
  dealPizza: any = {},
  discountPercent = 0,
  preferenceFallback: any = {},
  parentItem: any = {},
) => {
  const discountableTotal = getDiscountableItemTotal(dealPizza);
  const calculatedDiscount = calculateDiscountAmount(discountableTotal, discountPercent);
  const existingDiscount = toNumber(
    dealPizza?.discount ?? dealPizza?.discAmt ?? dealPizza?.promoDiscount
  );
  const discountAmount = calculatedDiscount || existingDiscount;
  const discountedTaxes = getDiscountedTaxFields(dealPizza, discountPercent);
  const submitItemTotal = getSubmitItemTotal(dealPizza, discountAmount, discountPercent);
  const modifiers = Array.isArray(dealPizza?.modifiers)
    ? dealPizza.modifiers.map(normalizeModifierPayload)
    : [];
  const selectedItemsLevel = Array.isArray(dealPizza?.selectedItemsLevel)
    ? dealPizza.selectedItemsLevel.map(normalizeSelectedItemLevelPayload)
    : [];
  const pizzaSize =
    normalizePizzaSizePayload(dealPizza?.pizzaSize) ||
    normalizePizzaSizePayload(dealPizza?.config?.existingPizzaSize) ||
    normalizePizzaSizePayload(preferenceFallback?.pizzaSize);
  const spiceLevel =
    normalizeSpiceLevelPayload(dealPizza?.spiceLevel) ||
    normalizeSpiceLevelPayload(dealPizza?.config?.existingSpiceLevel) ||
    normalizeSpiceLevelPayload(preferenceFallback?.spiceLevel);
  const crustCandidates = [
    normalizePizzaCrustPayload(dealPizza?.pizzaCrust),
    normalizePizzaCrustPayload(dealPizza?.config?.existingPizzaCrust),
    normalizePizzaCrustPayload(preferenceFallback?.pizzaCrust),
  ].filter(Boolean);
  const pizzaCrust =
    crustCandidates.find((item: any) => toNumber(item?.crustId) > 0) ||
    crustCandidates[0] ||
    null;
  const baseSubmitDescription = getSubmitDescription(
    dealPizza?.fullDescription,
    dealPizza?.description,
    dealPizza?.pizzaName,
    dealPizza?.config?.pizzaName,
    dealPizza?.productName
  );
  const pizzaIndex =
    toNumber(dealPizza?.pizzaIndex) ||
    toNumber(dealPizza?.config?.pizzaIndex) ||
    toNumber(dealPizza?.PizzaNo) ||
    toNumber(dealPizza?.config?.PizzaNo) ||
    toNumber(dealPizza?.dtltopp_pizzaitem) ||
    toNumber(dealPizza?.config?.dtltopp_pizzaitem) ||
    toNumber(dealPizza?.lineIndex);
  const isServiceSub =
    toNumber(
      parentItem?.isServiceSub ??
        parentItem?.data?.isServiceSub ??
        dealPizza?.isServiceSub ??
        dealPizza?.config?.isServiceSub,
    ) === 1 ||
    /\bsubz?\b/i.test(
      String(
        parentItem?.fullDescription ||
          parentItem?.productName ||
          dealPizza?.fullDescription ||
          dealPizza?.description ||
          "",
      ),
    );
  const slotLabel = isServiceSub ? "Sub" : "Pizza";
  const submitDescription =
    pizzaIndex > 0 && !new RegExp(`\\b(?:Pizza|Sub)\\s+${pizzaIndex}\\b`, "i").test(baseSubmitDescription)
      ? `${baseSubmitDescription} ${slotLabel} ${pizzaIndex}`
      : baseSubmitDescription;
  const fallbackDealProductId = toNumber(
    dealPizza?.dealProductId ??
      dealPizza?.config?.dealProductId ??
      dealPizza?.sourceDealPizza?.productId,
  );

  return {
    productId: toNumber(
      dealPizza?.productId || dealPizza?.id || dealPizza?.config?.id || fallbackDealProductId,
    ),
    productCode: String(
      dealPizza?.productCode ||
        dealPizza?.config?.pizzaProductCode ||
        dealPizza?.pizzaName ||
        dealPizza?.config?.pizzaName ||
        dealPizza?.productName ||
        dealPizza?.fullDescription ||
        ""
    ),
    quantity: toNumber(dealPizza?.quantity, 1),
    totalUnitPrice: toNumber(
      dealPizza?.totalUnitPrice ?? dealPizza?.price ?? dealPizza?.subTotal
    ),
    discAmt: discountAmount,
    discperc: discountPercent || toNumber(dealPizza?.discperc),
    discount: discountAmount,
    subTotal: toNumber(dealPizza?.subTotal),
    description: submitDescription,
    PizzaNo: toNumber(
      dealPizza?.PizzaNo ??
        dealPizza?.pizzaNo ??
        dealPizza?.config?.PizzaNo ??
        dealPizza?.config?.pizzaNo ??
        dealPizza?.dtltopp_pizzaitem ??
        dealPizza?.config?.dtltopp_pizzaitem ??
        (pizzaIndex > 0 ? pizzaIndex : 0),
      0,
    ),
    taxId1: toNumber(dealPizza?.taxId1),
    taxId2: toNumber(dealPizza?.taxId2),
    taxId3: toNumber(dealPizza?.taxId3),
    taxRate1: toNumber(dealPizza?.taxRate1),
    taxRate2: toNumber(dealPizza?.taxRate2),
    taxRate3: toNumber(dealPizza?.taxRate3),
    tax1: discountedTaxes.tax1,
    tax2: discountedTaxes.tax2,
    tax3: discountedTaxes.tax3,
    totalTax1: discountedTaxes.tax1,
    totalTax2: discountedTaxes.tax2,
    totalTax3: discountedTaxes.tax3,
    itemTotal: submitItemTotal,
    totalValue: submitItemTotal,
    note: appendPreferenceLinesToNote(
      dealPizza?.note || dealPizza?.config?.note || "",
      pizzaSize,
      spiceLevel
    ),
    isPizzaDeal: toNumber(
      dealPizza?.isPizzaDeal ?? dealPizza?.isDeal ?? 0
    ),
    isPizzaItem: toNumber(
      dealPizza?.isPizzaItem ?? dealPizza?.isPizza ?? 1
    ),
    isSpecilityPizza: toNumber(
      dealPizza?.isSpecilityPizza ?? dealPizza?.isSpecialityPizza ?? 1
    ),
    isCreateyourown: toNumber(
      dealPizza?.isCreateyourown ?? dealPizza?.isCreateYourOwn ?? 0
    ),
    pizzaCrust,
    pizzaSize,
    spiceLevel,
    modifiers,
    selectedItemsLevel,
  };
};

const normalizeOrderDetailPayload = (item: any = {}, discountPercent = 0) => {
  const discountableTotal = getDiscountableItemTotal(item);
  const calculatedDiscount = calculateDiscountAmount(discountableTotal, discountPercent);
  const existingDiscount = toNumber(
    item?.discount ?? item?.discAmt ?? item?.promoDiscount ?? item?.discountAmount
  );
  const discountAmount = calculatedDiscount || existingDiscount;
  const discountedTaxes = getDiscountedTaxFields(item, discountPercent);
  const submitItemTotal = getSubmitItemTotal(item, discountAmount, discountPercent);
  const selectedDealPizzas = Array.isArray(item?.selectedDealPizzas)
    ? item.selectedDealPizzas
    : Array.isArray(item?.data?.selectedDealPizzas)
    ? item.data.selectedDealPizzas
    : [];
  const modifiers = Array.isArray(item?.modifiers)
    ? item.modifiers.map(normalizeModifierPayload)
    : [];
  const selectedItemsLevel = Array.isArray(item?.selectedItemsLevel)
    ? item.selectedItemsLevel.map(normalizeSelectedItemLevelPayload)
    : [];
  const preferenceFallback = getPizzaPreferencePayloadFallback(
    item?.description || item?.fullDescription || item?.data?.description || "",
  );
  const pizzaSize =
    normalizePizzaSizePayload(item?.pizzaSize) ||
    normalizePizzaSizePayload(preferenceFallback.pizzaSize);
  const spiceLevel =
    normalizeSpiceLevelPayload(item?.spiceLevel) ||
    normalizeSpiceLevelPayload(preferenceFallback.spiceLevel);
  const crustCandidates = [
    normalizePizzaCrustPayload(item?.pizzaCrust),
    normalizePizzaCrustPayload(item?.data?.config?.existingPizzaCrust),
    normalizePizzaCrustPayload(preferenceFallback.pizzaCrust),
  ].filter(Boolean);
  const pizzaCrust =
    crustCandidates.find((candidate: any) => toNumber(candidate?.crustId) > 0) ||
    crustCandidates[0] ||
    null;
  const submitDescription = getSubmitDescription(
    item?.fullDescription,
    item?.description,
    item?.productName
  );

  return {
    id: toNumber(item?.id || item?.productId),
    productId: toNumber(item?.productId || item?.id),
    productCode: String(item?.productCode || ""),
    description: submitDescription,
    quantity: toNumber(item?.quantity, 1),
    price: toNumber(item?.price),
    variationsPrice: toNumber(item?.variationsPrice),
    totalUnitPrice: toNumber(item?.totalUnitPrice ?? item?.price ?? item?.subTotal),
    discAmt: discountAmount,
    discperc: discountPercent || toNumber(item?.discperc),
    discount: discountAmount,
    subTotal: toNumber(item?.subTotal),
    itemTotal: submitItemTotal,
    totalValue: submitItemTotal,
    taxId1: toNumber(item?.taxId1),
    taxId2: toNumber(item?.taxId2),
    taxId3: toNumber(item?.taxId3),
    note: appendPreferenceLinesToNote(
      item?.note || item?.data?.config?.note || "",
      pizzaSize,
      spiceLevel
    ),
    tax1: discountedTaxes.tax1,
    tax2: discountedTaxes.tax2,
    tax3: discountedTaxes.tax3,
    totalTax1: discountedTaxes.tax1,
    totalTax2: discountedTaxes.tax2,
    totalTax3: discountedTaxes.tax3,
    status: String(item?.status || "new"),
    modifiers,
    selectedItemsLevel,
    isDeal: toNumber(item?.isDeal),
    isPizza: toNumber(item?.isPizza),
    isCreateYourOwn: toNumber(item?.isCreateYourOwn),
    isServiceSub: toNumber(item?.isServiceSub ?? item?.data?.isServiceSub),
    taxRate1: toNumber(item?.taxRate1),
    taxRate2: toNumber(item?.taxRate2),
    taxRate3: toNumber(item?.taxRate3),
    pizzaCrust,
    selectedDealPizzas: selectedDealPizzas.map((dealPizza: any) =>
      normalizeDealPizzaPayload(dealPizza, discountPercent, preferenceFallback, item)
    ),
    pizzaSize,
    spiceLevel,
  };
};

type InititalStateType = {
  loading: boolean;
  cart: any[];
  checkoutData: any;
  pizzaList: any[];
  userData: any;
  taxes: TaxesType[];
  settings: SettingType;
  showCart: boolean;
  showUserDetailModel: boolean;
  showDrawer: boolean;
  companyData?: CompanyData;
  apiBuildVersion?: string;
  sideBarMenu: SideBarItem[];
  availableMenuCategoryIds?: number[];
  selectedProduct: ProductType | undefined;
  validTimes: TimeItem[];
  gestData: any;
  deliveryType: string;
  showGlobalSearchModal: boolean;
  isSelectGuestUser: boolean;
  guestUserData: {
    address: string;
    tip: number;
    clientId: number;
    suitNo: string;
    buzzerNo: string;
    roomNo: string;
    firstName: string;
    lastName: string;
    address1: string;
    phoneNo: string;
    email: string;
    password: string;
    confirmPassword: string;
    onDate: string;
    onTime: string;
    selectedAddress: string;
  };
  cartBtnLoading: Boolean;
};

const getCachedUserData = () => {
  if (typeof window === "undefined") return undefined;

  try {
    const raw = window.localStorage.getItem("userData");
    return raw ? JSON.parse(raw) : undefined;
  } catch {
    return undefined;
  }
};

/*
OnlineOrderingStatus ✅
ClosePickupOrder✅
CloseDeliveryOrder✅
PickupOnlinePayment✅
DeliveryOnlineOrder✅
DontPayAtStorePickup✅
DontPayAtStoreDelivery✅
ShowCompanyName✅
ShowLogo 🔴
ApplyTakeOutDeliveryPrice🔴
ApplyDeliveryChargesAsKm 🔴
DontDisplayTipOption ✅
MinimumDeliveryOrder 🔴
OnlineDeliveryBoundry 🔴
AllowDiscountOnDelivery ✅
AllowDiscountOnPickup✅
OnlineSpecialDiscount ✅
DisplayLocationOnCheckout✅

extra flags 
AllowFutureDate
*/
const initialSetting = {
  OnlineCombineLineQty: 0,
  AllowFutureDate: 1,
  MapAPIKeySetting: "",
  EnableGiftcardPromo: 0,
  DisplayLocationOnCheckout: 0,
  AllowDiscountOnDelivery: 1,
  AllowDiscountOnPickup: 1,
  DontDisplayTipOption: 0,
  ApplyTakeOutDeliveryPrice: 0,
  DontDisplayPizzaSubLevel: 0,
  DisplayDefaultCrustOnline: 0,
  OnlineSpecialDiscount: 1,
  OnlineDeliveryBoundry: 0,
  ApplySpecialDiscountOnward: 0,
  MinimumDeliveryOrder: 0,
  DeliveryOrderOptions: 1,
  ReservationOrderOptions: 0,
  CloseReservationOrder: 0,
  DeptDisplayOption: "Both",
  ShowLogo: 0,
  ShowCompanyName: 1,
  PickupOrderOptions: 1,
  DontPayAtStorePickup: 1,
  DontPayAtStoreDelivery: 1,
  PaypalPickup: 0,
  PaypalDelivery: 0,
  AuthorizePickup: 0,
  AuthorizeDelivery: 0,
  MonerisPickup: 0,
  MonerisDelivery: 0,
  StripePickup: 0,
  StripeDelivery: 0,
  ElavonPickup: 0,
  ElavonDelivery: 0,
  TDPickup: 0,
  TDDelivery: 0,
  GlobalPickup: 0,
  GlobalDelivery: 0,
  CloverPickup: 0,
  CloverDelivery: 0,
  PickupOnlinePayment: 1,
  DeliveryOnlineOrder: 1,
  ApplyDeliveryChargesAsKm: 1,
  ApplySpecialDiscountAsTotal: 0,
  DisplayDefaultToppings: 0,
  OnlineOrderingStatus: 1,
  ClosePickupOrder: 1,
  CloseDeliveryOrder: 0,
  menuDirection: "",
  MinimumDeliveryOrderAmt: "",
  DefaultCrustName: "",
  showLogo: 0,
  themeColor: 0,
  itemPriceColor: 0,
  itemColor: 0,
  deptColor: 0,
  themeSelColor: 0,
  headerColor: 0,
  itemPriceBgColor: 0,
  clearCartOnBrowerExit: 0,

  // === ADDED BY SAMPATH – 2026-01-29 ===
  // New Header settings (Text color + Banner URL)
  // NOTE: URL is string, so it will stay string due to your merge logic: isNaN(+v) ? v : +v
  headerTextColor: 0,
  headerBannerUrl: "",
  // === END SAMPATH ===
};

const initialState: InititalStateType = {
  loading: true,
  cart: [],
  pizzaList: [],
  userData: getCachedUserData(),
  showCart: false,
  showUserDetailModel: false,
  showDrawer: false,
  taxes: [],
  settings: initialSetting,
  sideBarMenu: [],
  availableMenuCategoryIds: undefined,
  selectedProduct: undefined,
  checkoutData: undefined,
  validTimes: [],
  companyData: undefined,
  apiBuildVersion: undefined,
  gestData: undefined,
  deliveryType: "",
  showGlobalSearchModal: false,
  isSelectGuestUser: false,
  guestUserData: {
    address: "",
    tip: 0,
    clientId: 0,
    suitNo: "",
    buzzerNo: "",
    roomNo: "",
    firstName: "",
    lastName: "",
    address1: "",
    phoneNo: "",
    email: "",
    password: "",
    confirmPassword: "",
    onDate: format(new Date(), "yyyy-MM-dd"),
    onTime: format(new Date(), "HH:mm"),
    selectedAddress: "home",
  },
  cartBtnLoading: false,
};

export const mainSlice = createSlice({
  name: "counter",
  initialState,
  reducers: {
    setInitialData: (state, action: PayloadAction<any>) => {
      state.loading = false;
      state.sideBarMenu = action.payload.categories;
      state.taxes = action.payload.taxes;
      state.companyData = action.payload.companyData;
      state.apiBuildVersion =
        action.payload.buildVersion || action.payload.companyData?.buildVersion;
      state.pizzaList = [];
      state.settings = {
        ...initialSetting,
        ...(Object.entries(action.payload.settings).reduce(
          (obj, [k, v]: any) => ({ ...obj, [k]: isNaN(+v) ? v : +v }),
          {}
        ) as any),
      };
    },
    setdeliveryType: (state, action: PayloadAction<any>) => {
      state.deliveryType = action.payload;
    },
    setSettings: (state, action: PayloadAction<any>) => {
      state.settings = action.payload;
    },
    setCheckoutData: (state, action: PayloadAction<any>) => {
      state.checkoutData = action.payload;
    },
    updateCheckoutData: (state, action: PayloadAction<any>) => {
      state.checkoutData = { ...state.checkoutData, ...action.payload };
    },
    setSelected: (state, action: PayloadAction<any>) => {
      state.selectedProduct = action.payload;
    },
    setPizzaList: (state, action: PayloadAction<any>) => {
      state.pizzaList = action.payload;
    },
    setAvailableMenuCategoryIds: (state, action: PayloadAction<number[] | undefined>) => {
      state.availableMenuCategoryIds = action.payload;
    },
    setUserData: (state, action: PayloadAction<any>) => {
      state.userData = action.payload;
      if (typeof window !== "undefined") {
        if (action.payload) {
          window.localStorage.setItem("userData", JSON.stringify(action.payload));
        } else {
          window.localStorage.removeItem("userData");
        }
      }
    },
    setAllCartData: (state, action: PayloadAction<any>) => {
      state.cart = action.payload || [];
    },
    setGestData: (state, action: PayloadAction<any>) => {
      state.gestData = action.payload;
    },
    setCartBtnLoading: (state, action: PayloadAction<Boolean>) => {
      state.cartBtnLoading = action.payload;
    },
    addToCart: (state, action: PayloadAction<any>) => {
      const userId = state?.userData?.clientId;
      const item = action.payload;
      if (item?.data.isEdit) {
        if (userId) {
          const sanitizedItem = sanitizeCartPayload(item);
          api
            .post("/api/Cart/UpdateCart", [
              {
                ...sanitizedItem,
                cartId: state.cart[item.data.index].cartId,
                userId,
              },
            ])
            .then(({ data }) => {
              console.log("Added to cart", data);
            })
            .catch((e) => {
              console.log(e);
            });
        }
        state.cart[item.data.index] = item;
      } else if (item) {
        if (userId) {
          const sanitizedItem = sanitizeCartPayload(item);
          api
            .post("/api/Cart/AddCart", [{ ...sanitizedItem, cartId: 0, userId }])
            .then(({ data }) => {
              if (data.orderId) {
                console.log("Added to cart", data);
              }
            })
            .catch((e) => {
              console.log(e);
            });
          state.cart.push(item);
        }
      }
    },
    setValidTimes: (state, action: PayloadAction<any>) => {
      state.validTimes = action.payload;
    },
    removeFromCart: (state, action: PayloadAction<number>) => {
      const userId = state?.userData?.clientId;
      let cartId = -1;
      state.cart = state.cart.filter((item, i) => {
        if (i !== action.payload) return true;
        cartId = item.cartId;
        return false;
      });

      if (userId && cartId !== -1) {
        api
          .post("/api/Cart/DeleteCart?id=" + cartId)
          .then(({ data }) => {
            console.log("deleted from cart", data);
          })
          .catch((e) => {
            console.log(e);
          });
      }
    },
    removeAllFromCart: (state) => {
      state.cart = [];
      const userId = state?.userData?.clientId;
      if (userId) {
        api
          .post("/api/Cart/DeleteUserCart?userid=" + userId)
          .then(({ data }) => {
            console.log("Delete entire cart", data);
          })
          .catch((e) => {
            console.log(e);
          });
      }
    },
    toggleLoading: (state) => {
      state.loading = !state.loading;
    },
    toggleCart: (state) => {
      state.showCart = !state.showCart;
    },
    toggleDrawer: (state) => {
      state.showDrawer = !state.showDrawer;
    },
    toggleUserDetailModel: (state) => {
      state.showUserDetailModel = !state.showUserDetailModel;
    },
    toggleGlobalSearchModal: (state) => {
      state.showGlobalSearchModal = !state.showGlobalSearchModal;
    },
    setIsGuestUser: (state, action: PayloadAction<boolean>) => {
      state.isSelectGuestUser = action.payload;
    },
    resetStates: (state) => {
      state.cart = [];
    },
  },
});

export const fetchInitialData =
  (navigate: NavigateFunction) =>
  async (dispatch: AppDispatch, getState: any) => {
    const clientId = getState()?.main?.userData?.clientId;

    dispatch(toggleLoading());
    api
      .get("/api/initialData")
      .then(({ data }) => {
        document.title = data.companyData.companyName;

        //! update logo dynamically
        // let link: any = document.querySelector("link[rel~='icon']");
        // if (!link) {
        //   link = document.createElement("link");
        //   link.rel = "icon";
        //   document.getElementsByTagName("head")[0].appendChild(link);
        // }
        // link.href = "https://stackoverflow.com/favicon.ico";
        const search = new URLSearchParams(window.location.search);
        const firstCategoryId = data?.categories?.[0]?.id;
        if (!search.get("product") && firstCategoryId) {
          search.set("product", firstCategoryId);
          navigate(
            {
              pathname: window.location.pathname,
              search: search.toString(),
            },
            { replace: true }
          );
        }
        dispatch(setInitialData(data));
      })
      .catch((e) => {
        showApiErrorToast(e, "Failed to load initial data");
        console.error("Failed to load /api/initialData", e);
        dispatch(toggleLoading());
      });
    if (clientId) {
      api
        .get("/api/Cart/GetUserCart?userid=" + clientId)
        .then((res) => {
          dispatch(setAllCartData(res.data));
        })
        .catch((e) => {
          console.error("Failed to load user cart", e);
        });
    }
    api
      .get("/api/RestTime/GetResttimes")
      .then(({ data }) => {
        dispatch(setValidTimes(data));
      })
      .catch((e) => {
        console.error("Failed to load rest times", e);
      });
  };

export const placeOrder =
  ({
    tip,
    paymentType,
    setStage,
    deliveryCharge,
    notes = "",
    setShowPopup,
    setOrderTotal,
    orderType,
  }: any) =>
  (dispatch: any, getState: any) => {
    const { checkoutData, cart, settings, companyData } = getState().main;
    const resolvedOrderType = orderType || checkoutData?.orderType;
    const unavailableCartItems = getUnavailableCartItemsForOrderType(
      cart,
      resolvedOrderType
    );

    if (unavailableCartItems.length) {
      const itemNames = unavailableCartItems
        .map(({ item }) => item?.fullDescription || item?.productName || item?.name)
        .filter(Boolean)
        .join(", ");
      toast.error(
        `Some items are not available for ${
          checkoutData?.orderType === "Delivery" ? "Delivery" : "Pickup"
        }: ${itemNames}`
      );
      return;
    }

    const specialDiscountPercent = getSpecialDiscountPercent(
      settings,
      resolvedOrderType
    );
    const taxes = cart.reduce(
      (obj: any, itm: any) => {
        if (itm.taxId1) {
          obj.taxId1 = 1;
          obj.totalTax1 = +(
            obj.totalTax1 + getTaxAfterDiscount(itm.totalTax1, specialDiscountPercent)
          ).toFixed(5);
        }
        if (itm.taxId2) {
          obj.taxId2 = 2;
          obj.totalTax2 = +(
            obj.totalTax2 + getTaxAfterDiscount(itm.totalTax2, specialDiscountPercent)
          ).toFixed(5);
        }
        if (itm.taxId3) {
          obj.taxId3 = 3;
          obj.totalTax3 = +(
            obj.totalTax3 + getTaxAfterDiscount(itm.totalTax3, specialDiscountPercent)
          ).toFixed(5);
        }

        return obj;
      },
      {
        taxId1: 0,
        taxId2: 0,
        taxId3: 0,
        totalTax1: 0,
        totalTax2: 0,
        totalTax3: 0,
      }
    );

    const subTotal =
      checkoutData.finalBill.find((i: any) => i.title === "SubTotal")?.amt ?? 0;
    const resolvedTip = +Number(tip ?? checkoutData?.tip ?? 0).toFixed(2);
    const resolvedDeliveryCharge =
      resolvedOrderType === "Delivery"
        ? +Number(deliveryCharge ?? checkoutData?.deliveryCharge ?? 0).toFixed(2)
        : 0;
    const selectedDeliveryAddressType =
      checkoutData?.selectedAddress === "office" ? "Office" : "Home";
    const resolvedDeliveryAddress =
      resolvedOrderType === "Delivery"
        ? selectedDeliveryAddressType === "Office"
          ? checkoutData?.address1 || checkoutData?.address || ""
          : checkoutData?.address || checkoutData?.address1 || ""
        : checkoutData?.address || "";
    const onlineSpecialDiscountAmount = calculateDiscountAmount(
      subTotal,
      specialDiscountPercent
    );

    const orderDetails = cart.map((item: any) =>
      normalizeOrderDetailPayload(item, specialDiscountPercent)
    );
    const summaryDiscountAmount = +orderDetails
      .reduce(
        (sum: number, item: any) =>
          sum + Number(item?.discount || item?.discAmt || 0),
        0,
      )
      .toFixed(2);
    const summaryDiscountPercent =
      specialDiscountPercent ||
      orderDetails.find((item: any) => Number(item?.discperc || 0) > 0)?.discperc ||
      0;
    const finalDiscountAmount = summaryDiscountAmount || onlineSpecialDiscountAmount;
    const savedTaxTotal = +(
      Number(taxes.totalTax1 || 0) +
      Number(taxes.totalTax2 || 0) +
      Number(taxes.totalTax3 || 0)
    ).toFixed(2);
    const finalOrderTotal = +(
      subTotal -
      finalDiscountAmount +
      savedTaxTotal +
      resolvedDeliveryCharge +
      resolvedTip
    ).toFixed(2);

    const params = {
      clientId: checkoutData.clientId,
      clientName: checkoutData.firstName,
      paymentType,
      ...taxes,
      stationId: 0, // static
      subTotal,
      notes: notes,
      deliveryCharge: resolvedDeliveryCharge,
      orderTotal: finalOrderTotal,
      promoCode: "", // static
      serviceCharge: "", // static
      promoDiscount: finalDiscountAmount,
      discount: finalDiscountAmount,
      discperc: summaryDiscountPercent,
      serviceFee: 0, // static
      bagFee: 0, // static
      tip: resolvedTip,
      id: 0, // static
      suitNo: "", // blank
      buzzerNo: "", // blank
      roomNo: "", // blank
      firstName: checkoutData.firstName,
      lastName: checkoutData.lastName,
      email: checkoutData.email,
      address: resolvedDeliveryAddress,
      address1: "",
      AddressType: selectedDeliveryAddressType,
      date: null, //null
      time: null, //null
      orderType: resolvedOrderType,
      onDate: moment(checkoutData.onDate).format("MM/DD/YYYY"),
      onTime: checkoutData.onTime + ":00",
      orderDetails,
      onDateTime: formatCurrentProjectDateTime(companyData?.timeZone),
    };
    const orderDTO = { ...params };
    delete (orderDTO as any).orderDetails;

    api
      .post("/api/OrderSubmition", {
        ...params,
        orderDTO,
        OrderDTO: orderDTO,
      })
      .then(({ data }) => {
        setStage?.("CART");
        dispatch(setCheckoutData(undefined));
        dispatch(setSelected(undefined));
        // dispatch(setGestData(undefined));
        dispatch(toggleCart());
        dispatch(removeAllFromCart());
        // toast.success("Order Places Successfully!");
        setShowPopup?.(true);
        setOrderTotal?.(params.orderTotal);
      })
      .catch((e) => {
        setShowPopup?.(false);
        console.error("Order submission failed", {
          status: e?.response?.status,
          response: e?.response?.data,
          payload: params,
        });
        showApiErrorToast(e);
      });
  };
export const {
  toggleLoading,
  removeAllFromCart,
  toggleDrawer,
  setPizzaList,
  setAvailableMenuCategoryIds,
  setInitialData,
  setUserData,
  setSelected,
  addToCart,
  removeFromCart,
  toggleCart,
  setAllCartData,
  setCheckoutData,
  setSettings,
  toggleUserDetailModel,
  updateCheckoutData,
  setValidTimes,
  setGestData,
  setdeliveryType,
  toggleGlobalSearchModal,
  setIsGuestUser,
  resetStates,
  setCartBtnLoading,
} = mainSlice.actions;

export default mainSlice.reducer;
